import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Star, Package, RotateCcw, Search } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/lib/store";
import { DeliveryBadge, inr } from "@/components/badges";

export default function BuyerHome() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ categories: [], zones: [] });
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, addToCart, zoneId, setZoneId } = useStore();

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const express = params.get("express") === "1";
  const inStock = params.get("stock") === "1";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user && user.role === "buyer") api.get("/orders").then((r) => setOrders(r.data.slice(0, 3))).catch(() => {});
  }, [user]);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: { q, category, brand, express, in_stock: inStock } })
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [q, category, brand, express, inStock]);

  const brands = useMemo(() => [...new Set(products.map((p) => p.brand).filter(Boolean))].sort(), [products]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="buyer-home">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline-label">Procurement</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Source materials</h1>
        </div>
        <select data-testid="buyer-zone-select" value={zoneId} onChange={(e) => setZoneId(e.target.value)} className="input-b2b !w-auto">
          <option value="">Deliver to: select zone</option>
          {meta.zones.map((z) => <option key={z.id} value={z.id}>{z.name}{z.express_supported ? "" : " (scheduled only)"}</option>)}
        </select>
      </div>

      {orders.length > 0 && (
        <div className="mt-6 card-surface p-4" data-testid="reorder-strip">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2"><RotateCcw className="h-3.5 w-3.5" /> Quick reorder</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {orders.map((o) => (
              <Link key={o.id} to={`/orders/${o.id}`} data-testid={`reorder-${o.order_no}`}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 hover:border-primary/50">
                {o.order_no} · {o.items.length} items · {inr(o.total)}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-[240px_1fr] gap-6">
        <aside className="card-surface p-4 h-fit space-y-5" data-testid="filter-panel">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input data-testid="buyer-search-input" value={q} onChange={(e) => setParam("q", e.target.value)}
              placeholder="Search SKU, brand…" className="input-b2b !pl-9" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Category</p>
            <button data-testid="filter-category-all" onClick={() => setParam("category", "")}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${!category ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-600"}`}>All</button>
            {(meta.category_groups || []).map((g) => (
              <div key={g.id} className="mt-3">
                <p className="text-[10px] font-mono2 uppercase tracking-wider text-slate-400 mb-1.5">{g.name}</p>
                <div className="flex flex-wrap gap-1.5">
                  {meta.categories.filter((c) => c.group === g.id).map((c) => (
                    <button key={c.id} data-testid={`filter-category-${c.id}`} onClick={() => setParam("category", c.id)}
                      className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${category === c.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-600"}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Brand</p>
            <select data-testid="filter-brand-select" value={brand} onChange={(e) => setParam("brand", e.target.value)} className="input-b2b">
              <option value="">All brands</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 font-medium">
            <input type="checkbox" data-testid="filter-express-toggle" checked={express} onChange={(e) => setParam("express", e.target.checked ? "1" : "")} className="h-4 w-4 accent-amber-500" />
            Express-eligible only
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 font-medium">
            <input type="checkbox" data-testid="filter-stock-toggle" checked={inStock} onChange={(e) => setParam("stock", e.target.checked ? "1" : "")} className="h-4 w-4 accent-amber-500" />
            In stock only
          </label>
        </aside>

        <div>
          {loading ? (
            <p className="text-sm text-slate-500 py-12 text-center" data-testid="products-loading">Loading catalog…</p>
          ) : products.length === 0 ? (
            <p className="text-sm text-slate-500 py-12 text-center" data-testid="products-empty">No materials match these filters.</p>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="product-grid">
              {products.map((p) => (
                <div key={p.id} data-testid={`product-card-${p.sku}`} className="card-surface overflow-hidden flex flex-col hover:shadow-md transition-shadow">
                  <Link to={`/product/${p.id}`} className="block h-36 overflow-hidden bg-slate-100">
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover" loading="lazy" />
                  </Link>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[11px] font-mono2 text-slate-400">{p.sku}</p>
                      <DeliveryBadge mode={p.express_eligible ? "express" : "scheduled"} />
                    </div>
                    <Link to={`/product/${p.id}`} className="mt-1.5 font-semibold text-sm text-slate-900 leading-snug hover:text-primary">{p.name}</Link>
                    <p className="mt-1 text-xs text-slate-500">{p.brand} · MOQ {p.moq} {p.unit}</p>
                    <p className="mt-1 text-xs text-slate-500 flex items-center gap-1">
                      {p.vendor_name}
                      {p.vendor_rating > 0 && <span className="inline-flex items-center gap-0.5 text-amber-600 font-semibold"><Star className="h-3 w-3 fill-amber-500 text-amber-500" />{p.vendor_rating}</span>}
                    </p>
                    <div className="mt-auto pt-3 flex items-center justify-between">
                      <div>
                        <p className="font-display text-lg font-bold text-slate-900">{inr(p.price)}<span className="text-xs font-normal text-slate-500">/{p.unit}</span></p>
                        <p className={`text-[11px] font-semibold flex items-center gap-1 ${p.stock > 0 ? "text-emerald-600" : "text-red-600"}`}>
                          <Package className="h-3 w-3" />{p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}
                        </p>
                      </div>
                      <button data-testid={`add-to-cart-${p.sku}`} disabled={p.stock <= 0} onClick={() => addToCart(p)}
                        className="btn-dark !px-3 !py-2 text-xs">Add</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
